import urlJoin from 'url-join'
import { useStoreConfig } from '~/store/storeConfig'
import { useCategoryStore } from '~/store/categoryStore'
import { useProductContentStore } from '~/store/productContentStore'

import getPathWithoutLocale from '~/utils/routing/getPathWithoutLocale'
import isLocalePresentInUrl from '~/utils/routing/isLocalePresentInUrl'
import sendLocaleChange from '~/utils/locale/sendLocaleChange'
import type { CategoryItem } from '~/types/searchTypes'
import { useProductStore } from '~/store/product'
import buildProductUrl from '~/utils/routing/buildProductUrl'
import { useCategoriesByKeysStore } from '~/store/categoriesByKeysStore'

export const useLocale = () => {
  const storeConfig = useStoreConfig()
  const categoryStore = useCategoryStore()
  const router = useRouter()
  const productContent = useProductContentStore()

  const changeLocale = async (newLocale: string) => {
    const currentRoute = router.currentRoute.value
    const currentRoutePath = currentRoute.fullPath

    if (currentRoute.meta.key === 'product') {
      const productStore = useProductStore()

      if (!storeConfig.getIsLoaded) {
        await storeConfig.getStoreConfig()
      }

      const newLanguageId = storeConfig.getSite?.languages?.find(
        (lang) => lang.iso_code.toLowerCase() === newLocale
      )?.id

      const newUrl = buildProductUrl(
        productStore.getFriendlyUrls.find((urlData) => urlData.language_id === newLanguageId)?.value,
        productStore.getVariantId,
        newLocale
      )

      await sendLocaleChange(newLocale)

      await navigateTo(newUrl)
    } else if (currentRoute.meta.key === 'category') {
      // Prepare data to fetch new categories.
      const newLanguageId = storeConfig.getSite?.languages?.find(
        (lang) => lang.iso_code.toLowerCase() === newLocale
      )?.id

      const newLanguageCategoryKey = storeConfig.allQueryKeys?.find(
        (querySet) => querySet.languageId === newLanguageId
      )?.categoryDocKey

      if (!newLanguageCategoryKey) {
        return
      }

      const categoriesByKeysStore = useCategoriesByKeysStore()

      const nextCategories = await categoriesByKeysStore.getByCategoryDocAndLocale(newLanguageCategoryKey, newLocale)
      // Get current route data
      const currentRouteSlug = productContent.getCategory?.slug ?? ''

      const currentRouteQuery = currentRoute.query
      const currentRouteId = categoryStore.getCategories.find(
        (category: CategoryItem) => category.slug === currentRouteSlug
      )?.id

      // Find alternative category in new locale categories.
      const nextRouteSlug = nextCategories.find((category: CategoryItem) => category.id === currentRouteId)

      await sendLocaleChange(newLocale)

      await navigateTo({
        path: urlJoin('/', newLocale, 'c', nextRouteSlug?.slug ?? ''),
        query: currentRouteQuery,
      })
    } else {
      const pathnameWithoutLocale = isLocalePresentInUrl(currentRoutePath)
        ? getPathWithoutLocale(currentRoute.fullPath)
        : currentRoutePath

      await sendLocaleChange(newLocale)

      await navigateTo({
        path: urlJoin('/', newLocale, pathnameWithoutLocale),
        query: currentRoute.query,
      })
    }
  }

  return {
    changeLocale,
  }
}
